import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { AlertCircle, ChevronDown } from "lucide-react";
import { ScanMFA } from '@/components/ScanMFA';

const Signup = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Legal checkboxes
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [aiDisclaimerAccepted, setAiDisclaimerAccepted] = useState(false);
  const [employmentDisclaimerAccepted, setEmploymentDisclaimerAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaQrCode, setMfaQrCode] = useState('');
  
  // Collapsible states for each section
  const [termsOpen, setTermsOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [employmentOpen, setEmploymentOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  
  const navigate = useNavigate();

  // Check if all required checkboxes are checked
  const canSubmit = termsAccepted && aiDisclaimerAccepted && employmentDisclaimerAccepted;

  const saveUserAgreement = async (userId: string) => {
    try {
      // Get user agent for legal tracking
      const userAgent = navigator.userAgent;
      
      const { error } = await supabase
        .from('user_agreements')
        .insert({
          user_id: userId,
          terms_accepted: true,
          privacy_accepted: true,
          ai_disclaimer_acknowledged: true,
          employment_outcome_acknowledged: true,
          marketing_opt_in: marketingOptIn,
          marketing_opt_in_date: marketingOptIn ? new Date().toISOString() : null,
          user_agent: userAgent,
          terms_version: '2025-10-25',
          privacy_version: '2025-10-25'
        });

      if (error) {
        console.error('Failed to save user agreement:', error);
        // Don't block signup if agreement save fails
      }
    } catch (error) {
      console.error('Error saving user agreement:', error);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canSubmit) {
      setShowError(true);
      toast.error("Please accept the required terms before creating your account");
      return;
    }
    
    setLoading(true);
    setShowError(false);

    try {
      const { data, error} = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/app`,
        },
      });

      if (error) throw error;

      // Save agreement data
      if (data.user?.id) {
        await saveUserAgreement(data.user.id);
        
        // Generate MFA secret with proper base32 encoding
        const bytes = crypto.getRandomValues(new Uint8Array(20));
        const base32chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
        let secret = '';
        let bits = 0;
        let value = 0;
        
        for (let i = 0; i < bytes.length; i++) {
          value = (value << 8) | bytes[i];
          bits += 8;
          while (bits >= 5) {
            secret += base32chars[(value >>> (bits - 5)) & 31];
            bits -= 5;
          }
        }
        if (bits > 0) {
          secret += base32chars[(value << (5 - bits)) & 31];
        }
        
        // Pad to make it a multiple of 8 for proper base32
        while (secret.length % 8 !== 0) {
          secret += '=';
        }
        
        const qrCodeUrl = `otpauth://totp/LoopBaan.ai:${encodeURIComponent(email)}?secret=${secret}&issuer=LoopBaan.ai&algorithm=SHA1&digits=6&period=30`;
        
        // Store MFA secret in database
        await supabase
          .from('mfa_secrets')
          .insert({
            user_id: data.user.id,
            secret,
            qr_code_url: qrCodeUrl,
            enabled: false
          });
        
        setMfaSecret(secret);
        setMfaQrCode(qrCodeUrl);
        
        // Send welcome email
        try {
          await supabase.functions.invoke('send-welcome-email', {
            body: {
              user_id: data.user.id,
              email: email,
              first_name: fullName.split(' ')[0] || fullName,
            },
          });
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError);
          // Don't block signup if welcome email fails
        }
      }

      // Always require MFA setup for new users
      toast.success("Account created! Please set up two-factor authentication to secure your account.");
      setShowMfaSetup(true);
    } catch (error: any) {
      if (error.message?.includes("User already registered")) {
        toast.error("An account with this email already exists. Please log in instead.");
      } else {
        toast.error(error.message || "Failed to create account");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/10 via-accent/5 to-background px-4 py-8">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <Logo className="h-20 w-20" />
            <span className="text-2xl font-bold">LoopBaan.ai</span>
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create Your Account</CardTitle>
            <CardDescription>Get started with your AI-powered career tools</CardDescription>
          </CardHeader>
          <form onSubmit={handleSignup}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <p className="text-xs text-muted-foreground">At least 6 characters</p>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm font-medium mb-4">
                  Before creating your account, please review and accept the following:
                </p>
                
                {showError && !canSubmit && (
                  <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-destructive">
                      Please accept the Terms of Service, Privacy Policy, and required acknowledgments before creating your account.
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  {/* Required Checkbox 1: Terms and Privacy */}
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="terms"
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                      className={`mt-1 ${showError && !termsAccepted ? 'border-destructive' : ''}`}
                    />
                    <div className="flex-1">
                      <Collapsible open={termsOpen} onOpenChange={setTermsOpen}>
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="terms"
                            className="text-sm font-medium leading-relaxed cursor-pointer"
                          >
                            I agree to the{" "}
                            <Link to="/terms" className="text-primary hover:underline" target="_blank">
                              Terms of Service
                            </Link>{" "}
                            and{" "}
                            <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                              Privacy Policy
                            </Link>
                            <span className="text-destructive ml-1">*</span>
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className={`h-4 w-4 transition-transform ${termsOpen ? 'rotate-180' : ''}`} />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <p className="text-xs text-muted-foreground mt-2">
                            By checking this box, I acknowledge that I have read, understood, and agree to be bound by LoopBaan's Terms of Service and Privacy Policy.
                          </p>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </div>

                  {/* Required Checkbox 2: AI Disclaimer */}
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="aiDisclaimer"
                      checked={aiDisclaimerAccepted}
                      onCheckedChange={(checked) => setAiDisclaimerAccepted(checked as boolean)}
                      className={`mt-1 ${showError && !aiDisclaimerAccepted ? 'border-destructive' : ''}`}
                    />
                    <div className="flex-1">
                      <Collapsible open={aiOpen} onOpenChange={setAiOpen}>
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="aiDisclaimer"
                            className="text-sm font-medium leading-relaxed cursor-pointer"
                          >
                            I understand that AI-generated content may contain errors
                            <span className="text-destructive ml-1">*</span>
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className={`h-4 w-4 transition-transform ${aiOpen ? 'rotate-180' : ''}`} />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <p className="text-xs text-muted-foreground mt-2">
                            I acknowledge that LoopBaan uses artificial intelligence to generate resumes and cover letters, and I understand that:
                            <br />• AI-generated content may contain errors or inaccuracies
                            <br />• I am solely responsible for reviewing and verifying all content
                            <br />• LoopBaan does not guarantee the accuracy of AI-generated content
                            <br />• I will carefully review all resumes and cover letters before submitting them to employers
                          </p>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </div>

                  {/* Required Checkbox 3: Employment Outcome Disclaimer */}
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="employmentDisclaimer"
                      checked={employmentDisclaimerAccepted}
                      onCheckedChange={(checked) => setEmploymentDisclaimerAccepted(checked as boolean)}
                      className={`mt-1 ${showError && !employmentDisclaimerAccepted ? 'border-destructive' : ''}`}
                    />
                    <div className="flex-1">
                      <Collapsible open={employmentOpen} onOpenChange={setEmploymentOpen}>
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="employmentDisclaimer"
                            className="text-sm font-medium leading-relaxed cursor-pointer"
                          >
                            I acknowledge there is no guarantee of employment outcomes
                            <span className="text-destructive ml-1">*</span>
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className={`h-4 w-4 transition-transform ${employmentOpen ? 'rotate-180' : ''}`} />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <p className="text-xs text-muted-foreground mt-2">
                            I understand that using LoopBaan does not guarantee job interviews, job offers, or employment. Results depend on many factors beyond LoopBaan's control, including my qualifications, market conditions, and employer preferences.
                          </p>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </div>

                  {/* Optional Checkbox: Marketing */}
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="marketing"
                      checked={marketingOptIn}
                      onCheckedChange={(checked) => setMarketingOptIn(checked as boolean)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Collapsible open={marketingOpen} onOpenChange={setMarketingOpen}>
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="marketing"
                            className="text-sm font-medium leading-relaxed cursor-pointer"
                          >
                            Send me helpful tips and product updates <span className="text-muted-foreground">(optional)</span>
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className={`h-4 w-4 transition-transform ${marketingOpen ? 'rotate-180' : ''}`} />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <p className="text-xs text-muted-foreground mt-2">
                            I would like to receive occasional emails with resume tips, job search advice, and product updates. I can unsubscribe anytime.
                          </p>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t text-xs text-muted-foreground space-y-2">
                  <p>By creating an account, you confirm that:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>You are at least 18 years old</li>
                    <li>You have the legal capacity to enter into binding contracts</li>
                    <li>All information you provide will be accurate and truthful</li>
                    <li>You will comply with all applicable laws and LoopBaan's Terms of Service</li>
                  </ul>
                  <p className="mt-2">
                    Your use of LoopBaan is subject to our Terms of Service and Privacy Policy.
                  </p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-4">
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading || !canSubmit}
              >
                {loading ? "Creating account..." : "Create Account"}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Log in
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>

      {showMfaSetup && mfaSecret && mfaQrCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <ScanMFA
            qrCodeUrl={mfaQrCode}
            secret={mfaSecret}
            onComplete={() => {
              setShowMfaSetup(false);
              navigate('/dashboard');
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Signup;
